"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.min = exports.max = exports.mapSumAll = exports.sumAll = exports.zero = exports.fromNullableNumber = exports.fromNumber = exports.Money = exports.stringToCurrency = exports.currencies = void 0;
const U_N = __importStar(require("./numeric"));
const U_P = __importStar(require("./percent"));
exports.currencies = ["USD", "EUR"];
const stringToCurrency = (currency) => (currency === "EUR" ? currency : "USD");
exports.stringToCurrency = stringToCurrency;
const moneyOp = (op) => (a) => (b) => ({
    USD: a.USD === undefined && b.USD === undefined
        ? undefined
        : op(a.USD ?? 0, b.USD ?? 0),
    EUR: a.EUR === undefined && b.EUR === undefined
        ? undefined
        : op(a.EUR ?? 0, b.EUR ?? 0)
});
const moneyAdd = moneyOp((a, b) => a + b);
const moneySub = moneyOp((a, b) => a - b);
const moneyTimes = (a) => (b) => ({
    USD: b.USD === undefined ? undefined : b.USD * a,
    EUR: b.EUR === undefined ? undefined : b.EUR * a
});
/**
 * Calculates the ratio between two amounts, considering a 1:1 ratio
 * conversion between currencies. If the denominator is zero, returns
 * undefined.
 *
 * Example:
 *
 * numerator = { USD: 20, EUR: 5, GBP: 5 }
 * denominator = { USD: 10, EUR: 10 }
 *
 * GBP is not considered in the calculation because it is not present in the
 * denominator.
 *
 * result = (20 + 5) / (10 + 10) = 25 / 20 = 1.25
 */
const moneyOver = (dividend) => (divisor) => {
    // take each pair of amounts for the same currency, for each currency that
    // has a denominator amount
    const pairs = exports.currencies
        .map(currency => [dividend[currency] ?? 0, divisor[currency]])
        .filter(([, b]) => b !== undefined);
    // if there are no pairs, return undefined
    if (pairs.length === 0) {
        return undefined;
    }
    // calculate the total denominator amount, considering a 1:1 ratio conversion
    // between currencies
    const totalDivisor = pairs.reduce((acc, [, b]) => acc + b, 0);
    // if the total denominator amount is zero, return undefined
    if (totalDivisor === 0) {
        return undefined;
    }
    // calculate the sum of the ratio of each denominator amount to the total
    // denominator amount
    const sumOfRatios = pairs.reduce((acc, [a]) => acc + a / totalDivisor, 0);
    return sumOfRatios;
};
/**
 * Represents a monetary amount as a composition of amounts in different
 * currencies. The purpose of this representation is to allow for
 * calculations that involve multiple currencies.
 */
class Money {
    _amounts;
    _trail;
    /**
     * Creates a new Money object.
     */
    constructor(_amounts, _trail = {
        kind: "MoneyCreate"
    }) {
        this._amounts = _amounts;
        this._trail = _trail;
    }
    toString = () => `Money(${JSON.stringify(this._amounts)})`;
    toJSON = () => ({
        kind: "Money",
        amounts: this._amounts,
        trail: this._trail
    });
    /**
     * @returns the amounts of this Money object as a map of currency to amount.
     */
    amounts = () => this._amounts;
    /**
     * @returns the USD component of this Money object, if any.
     */
    get USD() {
        return this._amounts.USD;
    }
    /**
     * @returns the EUR component of this Money object, if any.
     */
    get EUR() {
        return this._amounts.EUR;
    }
    /**
     * @returns the trail of this Money object.
     */
    trail = () => this._trail;
    /**
     * @returns a clone of the money object with the given trail label.
     */
    withLabel = (label) => new Money(this._amounts, { ...this._trail, label });
    /**
     * Overwrites the label of this Money object.
     */
    setLabel = (label) => {
        // eslint-disable-next-line functional/immutable-data
        this._trail.label = label;
        return this;
    };
    /**
     * Sums two Money objects.
     * The amounts of the two Money objects are summed for each currency.
     */
    plus = (other) => new Money(moneyAdd(this._amounts)(other._amounts), {
        kind: "MoneyAdd",
        left: this,
        right: other
    });
    /**
     * Subtracts two Money objects.
     * The amounts of the two Money objects are subtracted for each currency.
     */
    minus = (other) => new Money(moneySub(this._amounts)(other._amounts), {
        kind: "MoneySub",
        left: this,
        right: other
    });
    /**
     * Multiplies a Money object by a number.
     * The amounts of the Money object are multiplied by the number for each currency.
     */
    times = (a) => new Money(moneyTimes(a.value())(this._amounts), {
        kind: "MoneyTimes",
        left: this,
        right: a
    });
    /**
     * Divides two Money objects.
     * The amounts of the two Money objects are divided for each currency that
     * is present in the other Money object and the quotients are summed together.
     * If the denominator is zero, returns undefined.
     */
    over = (other) => {
        const ratio = moneyOver(this._amounts)(other._amounts);
        if (ratio === undefined) {
            return undefined;
        }
        return new U_N.Numeric(ratio, {
            kind: "MoneyOver",
            left: this,
            right: other
        });
    };
    /**
     * Divides two Money objects.
     * The amounts of the two Money objects are divided for each currency that
     * is present in the other Money object and the quotients are summed together.
     * If the denominator is zero, returns orElse wrapped in a Numeric, keeping the original trail.
     */
    overOrElse = (other, orElse) => {
        const ratio = moneyOver(this._amounts)(other._amounts);
        return new U_N.Numeric(ratio ?? orElse, {
            kind: "MoneyOver",
            left: this,
            right: other
        });
    };
    /**
     * @returns true if the amounts of the two Money objects are equal for each currency.
     */
    eq = (other) => this._amounts.USD === other._amounts.USD &&
        this._amounts.EUR === other._amounts.EUR;
    /**
     * @returns true if the amounts of the two Money objects are not equal for any currency.
     */
    neq = (other) => !this.eq(other);
    /**
     * @returns true if the Money object values are different from zero.
     */
    neqZero = () => !this.eqZero();
    /**
     * @returns true if the Money object values are equal to zero.
     */
    eqZero = () => (this.EUR ?? 0) === 0 && (this.USD ?? 0) === 0;
    /**
     * @returns true if all the amounts are greater than 0, false otherwise.
     */
    gtZero = () => ((this.USD ?? 0) > 0 && (this.EUR ?? 0) >= 0) ||
        ((this.EUR ?? 0) > 0 && (this.USD ?? 0) >= 0);
    /**
     * @returns if the Money object has at least one currency greater than the other and the rest equal.
     */
    gt = (other) => ((this.USD ?? 0) > (other.USD ?? 0) &&
        (this.EUR ?? 0) >= (other.EUR ?? 0)) ||
        ((this.EUR ?? 0) > (other.EUR ?? 0) && (this.USD ?? 0) >= (other.USD ?? 0));
    /**
     * @returns if the Money object has all currency values grater or equal than the other.
     */
    gte = (other) => exports.currencies.every(currency => {
        const a = this._amounts[currency];
        const b = other._amounts[currency];
        return ((a === undefined && b === undefined) ||
            (a !== undefined && b === undefined && a >= 0) ||
            (a !== undefined && b !== undefined && a >= b));
    });
    /**
     * @returns true if the amounts of the two Money objects are less than or equal for each currency.
     */
    lte = (other) => other.gte(this);
    /**
     * Multiplies each currency amount for a percent
     */
    timesPercent = (percent) => this.times(percent.toDecimal());
    /**
     * Divides each currency amount for a percent
     *
     * Technically, this is not a division by a percent, but a multiplication by
     * the inverse of the percent.
     */
    overPercent = (percent) => percent.eq(U_P.zero)
        ? undefined
        : this.times(U_N.one.overOrElse(percent.toDecimal(), 0));
    /**
     * Normalizes the amounts of this Money object to a single currency.
     * IMPORTANT: currently a conversion rate of 1:1 is assumed between currencies.
     *
     * @returns the amount as a number
     */
    toNormalizedAmount = () => (this._amounts.USD ?? 0) + (this._amounts.EUR ?? 0);
    /**
     * Converts the amounts of this Money object to a single currency in USD.
     *
     * @param fx conversion rates
     * @returns a new Money object with the amounts converted to USD only
     */
    fxToUsd = (fx) => new Money({
        USD: (this.USD ?? 0) + (this.EUR ?? 0) * fx.eurToUsd
    }, {
        kind: "MoneyToSingleCurrency",
        value: this,
        currency: `EURUSD ${fx.eurToUsd.toFixed(4)}}`
    });
    /**
     * Converts the amounts of this Money object to a single currency in EUR.
     *
     * @param fx conversion rates
     * @returns a new Money object with the amounts converted to EUR only
     */
    fxToEur = (fx) => new Money({
        EUR: (this.EUR ?? 0) + (this.USD ?? 0) * fx.usdToEur
    }, {
        kind: "MoneyToSingleCurrency",
        value: this,
        currency: `USDEUR ${fx.usdToEur.toFixed(4)}}`
    });
    /**
     * Transforms this Money object to a new Money object with only the amount of
     * the provided currency.
     */
    selectCurrency = (currency = "USD") => new Money({
        [currency]: this._amounts[currency]
    }, {
        kind: "MoneyWithSingleCurrency",
        value: this,
        currency
    });
    /**
     * Clamps up the amounts of this Money amounts to zero if they are negative.
     *
     * Example:
     *
     *    Money({ USD: -10, EUR: 5 }).maxWithZero() // Money({ USD: 0, EUR: 5 })
     *
     */
    maxWithZero = () => new Money({
        USD: this.USD === undefined ? undefined : this.USD < 0 ? 0 : this.USD,
        EUR: this.EUR === undefined ? undefined : this.EUR < 0 ? 0 : this.EUR
    }, {
        kind: "MoneyMax",
        left: this,
        right: exports.zero
    });
}
exports.Money = Money;
/**
 * Creates a new Money object from a number.
 */
const fromNumber = (amount, currency, trail) => new Money({ [currency]: amount }, trail);
exports.fromNumber = fromNumber;
/**
 * Creates a new Money object from a nullable number, returning orElse if the
 * number is null or undefined.
 */
const fromNullableNumber = (amount, currency, trail) => amount !== null && amount !== undefined
    ? (0, exports.fromNumber)(amount, currency, trail)
    : amount;
exports.fromNullableNumber = fromNullableNumber;
/**
 * A constant, empty Money object.
 */
exports.zero = new Money({}, {
    kind: "MoneyCreate",
    label: "CONSTANT"
});
const sumAll = (items) => {
    let USD = 0;
    let hasUSD = false;
    let EUR = 0;
    let hasEUR = false;
    for (let i = 0; i < items.length; i++) {
        const itemUSD = items[i].USD;
        const itemEUR = items[i].EUR;
        if (itemUSD !== undefined) {
            USD += itemUSD;
            if (!hasUSD) {
                hasUSD = true;
            }
        }
        if (itemEUR !== undefined) {
            EUR += itemEUR;
            if (!hasEUR) {
                hasEUR = true;
            }
        }
    }
    return new Money({ USD: hasUSD ? USD : undefined, EUR: hasEUR ? EUR : undefined }, {
        kind: "MoneySum",
        items
    });
};
exports.sumAll = sumAll;
/**
 * Map a function over a list of values and sum the results.
 */
const mapSumAll = (f) => (items) => (0, exports.sumAll)(items.map(f));
exports.mapSumAll = mapSumAll;
/**
 * Returns the maximum of each currency between two Money objects.
 */
const max = (a, b) => {
    const amountsA = a.amounts();
    const amountsB = b.amounts();
    return new Money(exports.currencies.reduce((acc, currency) => amountsA[currency] === undefined && amountsB[currency] === undefined
        ? acc
        : {
            ...acc,
            [currency]: Math.max(amountsA[currency] ?? 0, amountsB[currency] ?? 0)
        }, {}), {
        kind: "MoneyMax",
        left: a,
        right: b
    });
};
exports.max = max;
/**
 * Returns the minimum of each currency between two Money objects.
 */
const min = (a, b) => {
    const amountsA = a.amounts();
    const amountsB = b.amounts();
    return new Money(exports.currencies.reduce((acc, currency) => amountsA[currency] === undefined && amountsB[currency] === undefined
        ? acc
        : {
            ...acc,
            [currency]: Math.min(amountsA[currency] ?? 0, amountsB[currency] ?? 0)
        }, {}), {
        kind: "MoneyMin",
        left: a,
        right: b
    });
};
exports.min = min;
